import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { bestFinds, priceComparison } from '@/mocks/products';
import { faqItems } from '@/mocks/faq';

const SITE_URL = import.meta.env.VITE_SITE_URL;

export default function Guide() {
  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <article>
          <header className="bg-background-100 py-16 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <nav className="text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-700">Buying Guide</span>
              </nav>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
                The Ultimate AllChinaBuy Spreadsheet Guide — How to Save Time &amp; Money in 2026
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                A complete step-by-step guide to using the AllChinaBuy Spreadsheet for finding trusted sellers, comparing prices, and safely purchasing products from Taobao, Weidian, and 1688.
              </p>
              <div className="flex items-center justify-center gap-4 mt-6 text-xs text-foreground-500">
                <span>By China Buying Research Team</span>
                <span>·</span>
                <span>Updated June 2026</span>
                <span>·</span>
                <span>12 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Introduction: Why You Need the AllChinaBuy Spreadsheet
                </h2>
                <p>
                  If you have ever tried shopping on Taobao, Weidian, or 1688, you know how overwhelming it can be. Thousands of sellers, products in Chinese, and no easy way to verify quality. That is exactly why the <strong>AllChinaBuy Spreadsheet</strong> was created — to give international buyers a curated, verified, and constantly updated resource for finding the best products at the lowest prices.
                </p>
                <p>
                  This guide will walk you through everything you need to know about using the AllChinaBuy Spreadsheet effectively, from understanding seller ratings to completing your first purchase safely. Whether you are a complete beginner or an experienced buyer, this guide will help you save time and money.
                </p>
                <p className="bg-primary-50 border border-primary-100 rounded-lg p-4 text-sm">
                  <strong className="text-primary-700">Quick Navigation:</strong> New to all of this? Start with our{' '}
                  <Link to="/" className="text-primary-600 underline font-medium">Best AllChinaBuy Spreadsheet 2026 homepage</Link>{' '}
                  for an overview of what is available.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  How to Use the AllChinaBuy Spreadsheet — Step by Step
                </h2>
                <div className="space-y-3">
                  {[
                    { step: '1', title: 'Browse the Categories', desc: 'Start by exploring the 15 product categories in the spreadsheet. From Sneakers and Hoodies to Bags and Accessories, each category contains verified product links organized for easy browsing.' },
                    { step: '2', title: 'Check Seller Ratings', desc: 'Every product entry includes a seller rating, quality score, and verification status. Focus on products from verified and premium sellers with ratings above 4.5 for the best experience.' },
                    { step: '3', title: 'Compare Prices', desc: 'Use the built-in price comparison data to see how much you are saving compared to retail prices. Most products offer savings of 60-80% off official retail.' },
                    { step: '4', title: 'Request QC Photos', desc: 'Before completing any purchase, request quality control photos from the seller. This allows you to verify the product condition and ensure it matches the listing.' },
                    { step: '5', title: 'Choose Shipping', desc: 'Select a shipping method that includes tracking and insurance. Our guides recommend trusted shipping agents that specialize in international deliveries from China.' },
                    { step: '6', title: 'Complete Your Purchase', desc: 'Use secure payment methods with buyer protection. Most platforms support international payment options, making it easy to complete transactions safely.' },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4 p-4 bg-background-100 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                        <p className="text-xs text-foreground-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Understanding Seller Tiers in the Spreadsheet
                </h2>
                <div className="overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800">Tier</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800">Rating Required</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800">Transactions</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800">Recommendation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-background-50">
                        <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs font-bold">Premium</span></td>
                        <td className="px-4 py-3 text-foreground-700">9.0+</td>
                        <td className="px-4 py-3 text-foreground-700">1000+</td>
                        <td className="px-4 py-3 text-foreground-700">Best choice — highest quality guarantee</td>
                      </tr>
                      <tr className="bg-background-100/50">
                        <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-bold">Verified</span></td>
                        <td className="px-4 py-3 text-foreground-700">8.0+</td>
                        <td className="px-4 py-3 text-foreground-700">500+</td>
                        <td className="px-4 py-3 text-foreground-700">Reliable — good balance of quality and value</td>
                      </tr>
                      <tr className="bg-background-50">
                        <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-bold">Trusted</span></td>
                        <td className="px-4 py-3 text-foreground-700">7.5+</td>
                        <td className="px-4 py-3 text-foreground-700">200+</td>
                        <td className="px-4 py-3 text-foreground-700">Decent — use QC photos for extra safety</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-4">
                  We recommend starting with <strong>Premium</strong> and <strong>Verified</strong> sellers for your first few purchases. As you gain experience navigating the platforms, you can explore Trusted sellers for even better deals.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Best Products Currently in the AllChinaBuy Spreadsheet
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {bestFinds.slice(0, 4).map((item) => (
                    <div key={item.product} className="bg-background-100 rounded-lg overflow-hidden border border-background-200">
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.product}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-semibold text-foreground-900">{item.product}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <span className="text-primary-600 font-bold">{item.price}</span>
                          <span className="text-accent-600">Quality: {item.quality}</span>
                          <span className="text-secondary-600">{item.seller}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Price Comparison: AllChinaBuy Spreadsheet vs. Retail
                </h2>
                <div className="overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800">Product</th>
                        <th className="text-center px-4 py-3 font-semibold text-foreground-800">Retail</th>
                        <th className="text-center px-4 py-3 font-semibold text-primary-700">Spreadsheet</th>
                        <th className="text-center px-4 py-3 font-semibold text-accent-700">Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceComparison.map((row, i) => (
                        <tr key={row.product} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                          <td className="px-4 py-3 font-medium text-foreground-900">{row.product}</td>
                          <td className="text-center px-4 py-3 text-foreground-600">{row.officialPrice}</td>
                          <td className="text-center px-4 py-3 font-semibold text-primary-700">{row.spreadsheetPrice}</td>
                          <td className="text-center px-4 py-3">
                            <span className="inline-flex px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs font-bold">{row.savings}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Pros and Cons of Using the AllChinaBuy Spreadsheet
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-primary-50 border border-primary-100 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-primary-700 mb-3 flex items-center gap-2">
                      <i className="ri-thumb-up-line"></i> Pros
                    </h3>
                    <ul className="space-y-2 text-sm text-foreground-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-primary-500 mt-0.5"></i>
                        Save 60-80% compared to retail prices
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-primary-500 mt-0.5"></i>
                        Verified sellers reduce scam risk
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-primary-500 mt-0.5"></i>
                        Updated weekly with fresh finds
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-primary-500 mt-0.5"></i>
                        Beginner-friendly with step-by-step guides
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-primary-500 mt-0.5"></i>
                        Covers 15 product categories
                      </li>
                    </ul>
                  </div>
                  <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-2">
                      <i className="ri-information-line"></i> Considerations
                    </h3>
                    <ul className="space-y-2 text-sm text-foreground-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-line text-secondary-500 mt-0.5"></i>
                        Shipping times can be 2-4 weeks internationally
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-line text-secondary-500 mt-0.5"></i>
                        Some platforms require translation tools
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-line text-secondary-500 mt-0.5"></i>
                        Returns can be more complex than domestic purchases
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-line text-secondary-500 mt-0.5"></i>
                        Quality varies between sellers — always use QC
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {faqItems.map((faq, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                      <p className="text-sm text-foreground-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Conclusion: Start Saving Today
                </h2>
                <p>
                  The <strong>AllChinaBuy Spreadsheet</strong> is the most efficient way to discover trusted sellers, verified products, and unbeatable prices from Chinese e-commerce platforms. Whether you are shopping for sneakers, clothing, bags, or accessories, our weekly-updated spreadsheet gives you everything you need to shop with confidence.
                </p>
                <p>
                  Ready to get started? Head back to our{' '}
                  <Link to="/" className="text-primary-600 underline font-medium">AllChinaBuy Spreadsheet homepage</Link>{' '}
                  and begin exploring the latest finds, or jump directly to the{' '}
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 underline font-medium">AllChinaBuy Sheet</a>{' '}
                  to start shopping now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer justify-center"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer justify-center"
                  >
                    AllChinaBuy Sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'The Ultimate AllChinaBuy Spreadsheet Guide — How to Save Time & Money in 2026',
        description: 'A complete step-by-step guide to using the AllChinaBuy Spreadsheet for finding trusted sellers, comparing prices, and safely purchasing products.',
        image: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg',
        author: {
          '@type': 'Organization',
          name: 'China Buying Research Team',
        },
        publisher: {
          '@type': 'Organization',
          name: 'allchinabuy.info',
        },
        datePublished: '2026-01-15',
        dateModified: '2026-06-25',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/guide`,
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_URL}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Buying Guide',
            item: `${SITE_URL}/guide`,
          },
        ],
      }) }} />
    </>
  );
}